import {
  CampaignCard,
  useCampaignsQuery,
  useProblematicCampaignsQuery,
  type Campaign,
} from "@/entities/campaign";
import {
  Alert,
  AlertContent,
  AlertDescription,
  AlertIndicator,
  AlertTitle,
  Button,
  SearchField,
  SearchFieldClearButton,
  SearchFieldGroup,
  SearchFieldInput,
  SearchFieldSearchIcon,
  Spinner,
} from "@heroui/react";
import { useState } from "react";
import { Link } from "react-router-dom";

export interface CampaignListProps {
  clientLogin?: string;
}

export const CampaignList = ({ clientLogin }: CampaignListProps = {}) => {
  const [search, setSearch] = useState("");
  const {
    data: response,
    isLoading,
    error,
  } = useCampaignsQuery({
    clientLogin,
    search,
  });

  const { data: problematicResponse } =
    useProblematicCampaignsQuery(clientLogin);
  const problemCount =
    problematicResponse?.total ?? problematicResponse?.data?.length ?? 0;

  const campaigns: Campaign[] = response?.data || [];

  return (
    <div className="flex flex-col gap-4">
      {/* Баннер-алёрт о наличии кампаний с проблемами */}
      {problemCount > 0 && (
        <Alert
          status="danger"
          className="w-full rounded-2xl bg-rose-500/10 border border-rose-500/30 text-rose-300"
        >
          <AlertIndicator />
          <AlertContent className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 w-full">
            <div>
              <AlertTitle className="text-sm font-semibold text-rose-400">
                Обнаружены кампании с проблемами ({problemCount})
              </AlertTitle>
              <AlertDescription className="text-xs line-clamp-2">
                Алгоритмы оптимизации выявили неэффективные площадки и нецелевой
                расход бюджета.
              </AlertDescription>
            </div>
            <Link
              to={clientLogin ? `/${clientLogin}/problems` : "/problems"}
              className="shrink-0"
            >
              <Button
                size="sm"
                variant="primary"
                className="bg-rose-600 hover:bg-rose-500 text-white font-medium flex items-center gap-1.5"
              >
                <span>Посмотреть проблемы</span>
              </Button>
            </Link>
          </AlertContent>
        </Alert>
      )}

      {/* Поисковая строка */}
      <SearchField
        value={search}
        onChange={setSearch}
        aria-label="Поиск по кампаниям Яндекс Директа"
      >
        <SearchFieldGroup>
          <SearchFieldSearchIcon />
          <SearchFieldInput placeholder="Поиск по названию кампании..." />
          <SearchFieldClearButton />
        </SearchFieldGroup>
      </SearchField>

      {/* Индикатор загрузки */}
      {isLoading && (
        <div className="py-16 flex flex-col items-center justify-center gap-3">
          <Spinner size="lg" />
          <p className="text-xs text-neutral-400">
            Загрузка и синхронизация кампаний с Яндекс Директом...
          </p>
        </div>
      )}

      {/* Ошибка запроса */}
      {!isLoading && error && (
        <div className="p-6 rounded-xl border border-red-200 bg-red-50/50 text-red-700 text-xs">
          Не удалось загрузить кампании. Пожалуйста, проверьте подключение или
          повторите попытку позже.
        </div>
      )}

      {/* Список карточек */}
      {!isLoading && !error && (
        <div className="flex flex-col gap-3">
          {campaigns.map((campaign) => {
            const campaignKey =
              campaign.campaign_id || campaign.id || campaign.name;
            return <CampaignCard key={campaignKey} campaign={campaign} />;
          })}

          {campaigns.length === 0 && (
            <div className="py-12 text-center text-neutral-400 text-sm">
              {search
                ? `Кампании по запросу «${search}» не найдены.`
                : "Кампании не найдены. Убедитесь, что в аккаунте созданы рекламные кампании в Яндекс Директе."}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
